import {
	Anchor,
	Card,
	Group,
	Loader,
	Stack,
	Text,
	Title,
	Tooltip,
} from "@mantine/core";
import { Link } from "react-router-dom";

import ActivityActor from "../activity/ActivityActor";
import ActivityCategoryIcon from "../activity/ActivityCategoryIcon";
import { toActionLabel, toExactTime, toRelativeTime } from "../../utils/activity";

import type { ActivityLogEntry } from "../../services/api";

interface RecentActivityProps {
	entries: ActivityLogEntry[];
	loading: boolean;
	error: string | null;
}

function RecentActivity({ entries, loading, error }: RecentActivityProps) {
	return (
		<Card
			shadow="sm"
			padding="lg"
			radius="md"
			withBorder
			mt="xl">
			<Title
				order={4}
				mb="md">
				Recent Activity
			</Title>

			{error ? (
				<Text
					c="red"
					size="sm">
					{error}
				</Text>
			) : loading && entries.length === 0 ? (
				// Only before the first result: a spinner on every refresh would
				// blank the list twice a minute
				<Group
					justify="center"
					py="md">
					<Loader size="sm" />
				</Group>
			) : entries.length === 0 ? (
				<Text
					c="dimmed"
					size="sm">
					No activity yet.
				</Text>
			) : (
				<Stack gap="sm">
					{entries.map((entry) => (
						<Group
							key={entry.id}
							justify="space-between"
							align="flex-start"
							wrap="nowrap">
							<Group
								align="flex-start"
								gap="sm"
								wrap="nowrap">
								<ActivityCategoryIcon category={entry.category} />
								<div>
									<Text
										size="sm"
										fw={500}>
										{toActionLabel(entry.action)}
									</Text>
									<Text size="sm">{entry.resource}</Text>
									<ActivityActor
										actorName={entry.actorName}
										actorType={entry.actorType}
									/>
								</div>
							</Group>
							<Tooltip label={toExactTime(entry.createdAt)}>
								<Text
									component="time"
									dateTime={entry.createdAt}
									size="xs"
									c="dimmed"
									style={{ whiteSpace: "nowrap" }}>
									{toRelativeTime(entry.createdAt)}
								</Text>
							</Tooltip>
						</Group>
					))}
				</Stack>
			)}

			<Group
				justify="flex-end"
				mt="md">
				<Anchor
					component={Link}
					to="/activity"
					size="sm">
					View all
				</Anchor>
			</Group>
		</Card>
	);
}

export default RecentActivity;
